/**
 * Default template for the Needs Map
 * 原体験に基づく学生の価値観フローのテンプレート
 */

import { Phase } from '../types/phaseTypes';
import { TopicNode } from '../types/nodeTypes';
import { TopicEdge } from '../types/edgeTypes';

/**
 * Template structure
 */
export interface Template {
  phases: Phase[];
  nodes: TopicNode[];
  edges: TopicEdge[];
}

/**
 * Default template for the Needs Map
 * 添付画像に基づいて修正したテンプレート
 */
export const defaultTemplate: Template = {
  // フェーズ（トピックのカテゴリ）定義
  phases: [
    {
      id: "fact_action",
      name: "事実・行動",
      description: "実際に行った行動や経験の事実",
      color: "#4287f5" // 青色
    },
    {
      id: "fact_detail",
      name: "事実・行動の具体",
      description: "行動や経験の詳細な内容",
      color: "#3db063" // 明度を落とした緑色（元は #42f584）
    },
    {
      id: "thought_feeling",
      name: "思考・感情",
      description: "経験から生じた考えや感情",
      color: "#f5a742" // オレンジ色
    },
    {
      id: "value",
      name: "価値観",
      description: "重視する価値や考え方",
      color: "#f54242" // 赤色
    },
    {
      id: "career_view",
      name: "キャリア観",
      description: "将来のキャリアに関する考え方",
      color: "#9c42f5" // 紫色
    },
    {
      id: "company",
      name: "紹介企業",
      description: "マッチする可能性のある企業",
      color: "#42c5f5" // 水色
    }
  ],
  
  // ノード（トピック）定義
  nodes: [
    // 事実・行動ノード
    {
      id: "high_effort",
      title: "高い水準で努力をした",
      phaseId: "fact_action",
      defaultImportance: 4,
      extractionPrompt: "会話から相手が高い水準で努力した経験や、通常以上の努力を続けた体験に関する言及を抽出してください。回数、期間、他者との比較など具体的な描写を含めてください。",
      contents: [],
      content: "",
      importance: 4,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "problem_solving",
      title: "自ら問題解決に取り組んだ",
      phaseId: "fact_action",
      defaultImportance: 4,
      extractionPrompt: "会話から相手が自ら課題を見つけ、問題解決に取り組んだ経験に関する言及を抽出してください。問題の内容、解決プロセス、成果などを含めてください。",
      contents: [],
      content: "",
      importance: 4,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "team_contribution",
      title: "チームに貢献した",
      phaseId: "fact_action",
      defaultImportance: 3,
      extractionPrompt: "会話から相手がチームやグループに貢献した経験に関する言及を抽出してください。具体的な役割、行動、チームへの影響などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    
    // 事実・行動の具体ノード
    {
      id: "continuous_effort",
      title: "継続的な努力",
      phaseId: "fact_detail",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が長期間にわたって継続的に努力した詳細について言及を抽出してください。期間、頻度、困難を乗り越えた方法などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "initiative_action",
      title: "自ら率先した行動",
      phaseId: "fact_detail",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が自ら率先して行動を起こした具体的な内容に関する言及を抽出してください。きっかけ、判断理由、具体的な行動などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "improvement_action",
      title: "改善のための行動",
      phaseId: "fact_detail",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が何かを改善するために具体的に行った行動に関する言及を抽出してください。改善対象、行動内容、工夫した点などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    
    // 思考・感情ノード
    {
      id: "fulfillment",
      title: "達成感・充実感",
      phaseId: "thought_feeling",
      defaultImportance: 4,
      extractionPrompt: "会話から相手が経験した達成感や充実感に関する言及を抽出してください。具体的な感情の描写、その感情が生じた状況などを含めてください。",
      contents: [],
      content: "",
      importance: 4,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "pride",
      title: "誇り・自信",
      phaseId: "thought_feeling",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が感じた誇りや自信に関する言及を抽出してください。何に対して誇りや自信を持ったのか、その感情の強さなどを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "satisfaction_from_others",
      title: "他者からの反応による満足感",
      phaseId: "thought_feeling",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が他者からの反応や評価によって感じた満足感に関する言及を抽出してください。他者の反応、それに対する感情などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    
    // 価値観ノード
    {
      id: "self_growth",
      title: "自己成長を重視する",
      phaseId: "value",
      defaultImportance: 4,
      extractionPrompt: "会話から相手が自己成長を重視していることを示す言及を抽出してください。成長に対する考え方、目標、成長のために行っていることなどを含めてください。",
      contents: [],
      content: "",
      importance: 4,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "contribution_value",
      title: "貢献することに価値を見出す",
      phaseId: "value",
      defaultImportance: 4,
      extractionPrompt: "会話から相手が他者や組織に貢献することに価値を見出していることを示す言及を抽出してください。貢献の意義、貢献したいと思う対象などを含めてください。",
      contents: [],
      content: "",
      importance: 4,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "recognition_value",
      title: "認められることに価値を見出す",
      phaseId: "value",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が他者から認められることに価値を見出していることを示す言及を抽出してください。認められたい内容、理由などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "challenge_value",
      title: "挑戦することに価値を見出す",
      phaseId: "value",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が挑戦することに価値を見出していることを示す言及を抽出してください。挑戦に対する姿勢、どのような挑戦をしたいかなどを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    
    // キャリア観ノード
    {
      id: "professional_growth",
      title: "専門性を高めるキャリア",
      phaseId: "career_view",
      defaultImportance: 4,
      extractionPrompt: "会話から相手が専門性を高めることを重視するキャリア志向に関する言及を抽出してください。目指す専門分野、理由などを含めてください。",
      contents: [],
      content: "",
      importance: 4,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "social_impact_career",
      title: "社会的影響力のあるキャリア",
      phaseId: "career_view",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が社会的影響力のあるキャリアを望んでいることを示す言及を抽出してください。どのような影響を与えたいか、理由などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "creative_career",
      title: "創造性を活かすキャリア",
      phaseId: "career_view",
      defaultImportance: 3,
      extractionPrompt: "会話から相手が創造性を活かしたキャリアを望んでいることを示す言及を抽出してください。どのように創造性を発揮したいか、理由などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    
    // 紹介企業ノード（例として）
    {
      id: "tech_company",
      title: "テクノロジー企業",
      phaseId: "company",
      defaultImportance: 3,
      extractionPrompt: "会話から相手がテクノロジー企業に関心を持っていることを示す言及を抽出してください。具体的な企業名、興味のある技術分野などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "consulting_company",
      title: "コンサルティング企業",
      phaseId: "company",
      defaultImportance: 3,
      extractionPrompt: "会話から相手がコンサルティング企業に関心を持っていることを示す言及を抽出してください。理由、興味のある分野などを含めてください。",
      contents: [],
      content: "",
      importance: 3,
      status: "pending",
      error: null,
      active: false
    }
  ],
  
  // エッジ（関連性）定義
  edges: [
    // 事実・行動から事実・行動の具体へ
    {
      id: "high_effort_to_continuous_effort",
      source: "high_effort",
      target: "continuous_effort",
      question: "どのような継続的な努力をしましたか？",
      strength: "strong",
      extractionPrompt: "会話から、高い水準の努力の具体的な内容に関する言及を抽出してください。努力の方法、期間、頻度などの詳細を探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "problem_solving_to_initiative_action",
      source: "problem_solving",
      target: "initiative_action",
      question: "問題解決のために具体的にどのような率先行動をとりましたか？",
      strength: "strong",
      extractionPrompt: "会話から、問題解決のために取った具体的な率先行動に関する言及を抽出してください。行動の内容、きっかけ、プロセスなどを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "team_contribution_to_improvement_action",
      source: "team_contribution",
      target: "improvement_action",
      question: "チームに貢献するために具体的にどのような改善行動をとりましたか？",
      strength: "medium",
      extractionPrompt: "会話から、チームへの貢献のために行った具体的な改善行動に関する言及を抽出してください。改善内容、プロセス、成果などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    
    // 事実・行動の具体から思考・感情へ
    {
      id: "continuous_effort_to_fulfillment",
      source: "continuous_effort",
      target: "fulfillment",
      question: "継続的な努力からどのような達成感を得ましたか？",
      strength: "strong",
      extractionPrompt: "会話から、継続的な努力によって得られた達成感や充実感に関する言及を抽出してください。具体的な感情の描写、感情が生じた瞬間などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "initiative_action_to_pride",
      source: "initiative_action",
      target: "pride",
      question: "率先した行動からどのような誇りや自信を得ましたか？",
      strength: "medium",
      extractionPrompt: "会話から、率先した行動によって得られた誇りや自信に関する言及を抽出してください。何に対して誇りを感じたか、自信につながった理由などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "improvement_action_to_satisfaction_from_others",
      source: "improvement_action",
      target: "satisfaction_from_others",
      question: "改善行動によって得られた他者からの反応はどうでしたか？",
      strength: "medium",
      extractionPrompt: "会話から、改善行動に対する他者からの反応とそれによる満足感に関する言及を抽出してください。他者の具体的な反応、それに対する感情などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    
    // 思考・感情から価値観へ
    {
      id: "fulfillment_to_self_growth",
      source: "fulfillment",
      target: "self_growth",
      question: "達成感・充実感は自己成長への価値観にどうつながりましたか？",
      strength: "strong",
      extractionPrompt: "会話から、達成感や充実感が自己成長を重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、価値観の具体的な内容などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "pride_to_challenge_value",
      source: "pride",
      target: "challenge_value",
      question: "誇り・自信は挑戦することへの価値観にどうつながりましたか？",
      strength: "medium",
      extractionPrompt: "会話から、誇りや自信が挑戦することを重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、挑戦に対する姿勢などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "satisfaction_from_others_to_contribution_value",
      source: "satisfaction_from_others",
      target: "contribution_value",
      question: "他者からの反応による満足感は貢献することへの価値観にどうつながりましたか？",
      strength: "strong",
      extractionPrompt: "会話から、他者からの反応による満足感が貢献することを重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、貢献したい理由などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "satisfaction_from_others_to_recognition_value",
      source: "satisfaction_from_others",
      target: "recognition_value",
      question: "他者からの反応による満足感は認められることへの価値観にどうつながりましたか？",
      strength: "medium",
      extractionPrompt: "会話から、他者からの反応による満足感が認められることを重視する価値観につながったことに関する言及を抽出してください。価値観の形成過程、認められたい理由などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    
    // 価値観からキャリア観へ
    {
      id: "self_growth_to_professional_growth",
      source: "self_growth",
      target: "professional_growth",
      question: "自己成長の価値観は専門性を高めるキャリア志向にどうつながりますか？",
      strength: "strong",
      extractionPrompt: "会話から、自己成長を重視する価値観が専門性を高めるキャリア志向につながることに関する言及を抽出してください。キャリア選択の理由、専門分野に対する考えなどを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "contribution_value_to_social_impact_career",
      source: "contribution_value",
      target: "social_impact_career",
      question: "貢献の価値観は社会的影響力のあるキャリア志向にどうつながりますか？",
      strength: "strong",
      extractionPrompt: "会話から、貢献することを重視する価値観が社会的影響力のあるキャリア志向につながることに関する言及を抽出してください。社会への貢献方法、影響を与えたい分野などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "challenge_value_to_creative_career",
      source: "challenge_value",
      target: "creative_career",
      question: "挑戦の価値観は創造性を活かすキャリア志向にどうつながりますか？",
      strength: "medium",
      extractionPrompt: "会話から、挑戦することを重視する価値観が創造性を活かすキャリア志向につながることに関する言及を抽出してください。創造的なキャリアへの興味、理由などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    
    // キャリア観から紹介企業へ
    {
      id: "professional_growth_to_tech_company",
      source: "professional_growth",
      target: "tech_company",
      question: "専門性を高めるキャリア志向はテクノロジー企業への関心にどうつながりますか？",
      strength: "medium",
      extractionPrompt: "会話から、専門性を高めるキャリア志向がテクノロジー企業への関心につながることに関する言及を抽出してください。関心のある企業タイプ、理由などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    },
    {
      id: "social_impact_career_to_consulting_company",
      source: "social_impact_career",
      target: "consulting_company",
      question: "社会的影響力のあるキャリア志向はコンサルティング企業への関心にどうつながりますか？",
      strength: "medium",
      extractionPrompt: "会話から、社会的影響力のあるキャリア志向がコンサルティング企業への関心につながることに関する言及を抽出してください。関心のある企業タイプ、理由などを探してください。",
      contents: [],
      content: "",
      status: "pending",
      error: null,
      active: false
    }
  ]
};

/**
 * Get a template by name
 */
export function getTemplateByName(name: string): Template {
  // Currently only the default template is available
  return defaultTemplate;
}